import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  Box,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  InputAdornment,
  alpha,
  Divider,
  Paper,
} from '@mui/material';
import {
  Search,
  LayoutDashboard,
  Briefcase,
  Users,
  Calculator,
  FileText,
  Package,
  Calendar,
  Settings,
  Sparkles,
  Plus,
  TrendingUp,
  ChartBar,
  Truck,
  Workflow,
  Activity,
  Clock,
  Command,
  ArrowRight,
  Zap,
  Star,
  History,
} from 'lucide-react';

const COMMANDS = [
  // Navigation
  { id: 'dashboard', label: 'Go to Dashboard', icon: LayoutDashboard, category: 'Navigation', keywords: ['home', 'main'] },
  { id: 'jobs', label: 'Go to Jobs', icon: Briefcase, category: 'Navigation', keywords: ['projects', 'work'] },
  { id: 'clients', label: 'Go to Clients', icon: Users, category: 'Navigation', keywords: ['customers', 'accounts'] },
  { id: 'estimates', label: 'Go to Estimates', icon: Calculator, category: 'Navigation', keywords: ['quotes', 'pricing'] },
  { id: 'permits', label: 'Go to Permits', icon: FileText, category: 'Navigation', keywords: ['approvals', 'licenses'] },
  { id: 'inventory', label: 'Go to Inventory', icon: Package, category: 'Navigation', keywords: ['stock', 'materials'] },
  { id: 'production', label: 'Go to Production', icon: Activity, category: 'Navigation', keywords: ['manufacturing', 'fabrication'] },
  { id: 'calendar', label: 'Go to Calendar', icon: Calendar, category: 'Navigation', keywords: ['schedule', 'dates'] },
  { id: 'reports-dashboard', label: 'Go to Analytics', icon: ChartBar, category: 'Navigation', keywords: ['reports', 'metrics'] },
  { id: 'settings', label: 'Go to Settings', icon: Settings, category: 'Navigation', keywords: ['config', 'preferences'] },
  
  // Quick Actions
  { id: 'action:new-job', label: 'Create New Job', icon: Plus, category: 'Actions', keywords: ['add', 'create', 'project'], action: 'create-job' },
  { id: 'action:new-estimate', label: 'Create New Estimate', icon: Plus, category: 'Actions', keywords: ['add', 'quote'], action: 'create-estimate' },
  { id: 'action:new-permit', label: 'Create New Permit', icon: Plus, category: 'Actions', keywords: ['add', 'application'], action: 'create-permit' },
  { id: 'action:schedule-install', label: 'Schedule Installation', icon: Calendar, category: 'Actions', keywords: ['book', 'install'], action: 'schedule-install' },
  
  // AI Features
  { id: 'ai-command', label: 'Open AI Command Hub', icon: Sparkles, category: 'AI Tools', keywords: ['assistant', 'help', 'ai'], highlight: true },
  { id: 'command-center', label: 'Open Command Center', icon: Zap, category: 'AI Tools', keywords: ['control', 'hub'], highlight: true },
  { id: 'workflow-monitor', label: 'Workflow Monitor', icon: Activity, category: 'AI Tools', keywords: ['automation', 'status'] },
  { id: 'concept-generator', label: 'AI Concept Generator', icon: Sparkles, category: 'AI Tools', keywords: ['design', 'ideas'], highlight: true },
  
  // Views
  { id: 'task-queue', label: 'Open Task Queue', icon: Clock, category: 'Views', keywords: ['tasks', 'todo', 'queue'] },
  { id: 'forecasting', label: 'Open Forecasting', icon: TrendingUp, category: 'Views', keywords: ['predict', 'future'] },
  { id: 'ohi-dashboard', label: 'Open OHI Dashboard', icon: Activity, category: 'Views', keywords: ['health', 'organization'] },
  { id: 'active-routes', label: 'View Active Routes', icon: Truck, category: 'Views', keywords: ['delivery', 'drivers'] },
];

const RECENT_SEARCHES = ['jobs', 'inventory', 'ai command'];

function CommandPalette({ open, onClose, onNavigate }) {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter commands based on search
  const filteredCommands = search.trim()
    ? COMMANDS.filter(cmd => {
        const searchLower = search.toLowerCase();
        return (
          cmd.label.toLowerCase().includes(searchLower) ||
          cmd.category.toLowerCase().includes(searchLower) ||
          cmd.keywords?.some(k => k.toLowerCase().includes(searchLower))
        );
      })
    : COMMANDS;

  // Group commands by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  // Flatten for navigation
  const flatCommands = Object.values(groupedCommands).flat();

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, flatCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (flatCommands[selectedIndex]) {
          handleSelect(flatCommands[selectedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [flatCommands, selectedIndex, onClose]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    selectedElement?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = (command) => {
    if (command.action) {
      // Handle action commands
      console.log('Action:', command.action);
    } else {
      // Navigate to page
      onNavigate?.(command.id.replace('action:', ''));
    }
    onClose();
  };

  let flatIndex = -1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'absolute',
          top: '15%',
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        },
      }}
      BackdropProps={{
        sx: { backdropFilter: 'blur(4px)', bgcolor: 'rgba(15, 23, 42, 0.4)' },
      }}
    >
      <Box onKeyDown={handleKeyDown}>
        {/* Search Input */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Type a command or search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color="#64748B" />
                </InputAdornment>
              ),
              endAdornment: (
                <Chip
                  label="ESC"
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: alpha('#64748B', 0.1),
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                />
              ),
              sx: { fontSize: '1.1rem' },
            }}
          />
        </Box>

        {/* Recent Searches (when no search) */}
        {!search.trim() && (
          <Box sx={{ px: 2, py: 1.5, bgcolor: alpha('#6366F1', 0.03) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <History size={14} color="#64748B" />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                RECENT
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {RECENT_SEARCHES.map((term, idx) => (
                <Chip
                  key={idx}
                  label={term}
                  size="small"
                  onClick={() => setSearch(term)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#6366F1', 0.1) },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Command List */}
        <List ref={listRef} sx={{ maxHeight: 400, overflow: 'auto', py: 1 }}>
          {Object.entries(groupedCommands).map(([category, commands]) => (
            <Box key={category}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ px: 2, py: 1, display: 'block' }}
              >
                {category.toUpperCase()}
              </Typography>
              {commands.map((command) => {
                flatIndex++;
                const isSelected = flatIndex === selectedIndex;
                const Icon = command.icon;

                return (
                  <ListItem
                    key={command.id}
                    data-index={flatIndex}
                    onClick={() => handleSelect(command)}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      borderRadius: 2,
                      mx: 1,
                      bgcolor: isSelected ? alpha('#6366F1', 0.1) : 'transparent',
                      '&:hover': {
                        bgcolor: alpha('#6366F1', 0.05),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: command.highlight
                            ? alpha('#8B5CF6', 0.1)
                            : alpha('#64748B', 0.1),
                        }}
                      >
                        <Icon
                          size={18}
                          color={command.highlight ? '#8B5CF6' : '#64748B'}
                        />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={command.label}
                      primaryTypographyProps={{
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: '0.9rem',
                      }}
                    />
                    {command.highlight && (
                      <Chip
                        label="AI"
                        size="small"
                        sx={{
                          height: 20,
                          bgcolor: alpha('#8B5CF6', 0.1),
                          color: '#8B5CF6',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {isSelected && (
                      <ArrowRight size={16} color="#6366F1" />
                    )}
                  </ListItem>
                );
              })}
            </Box>
          ))}

          {flatCommands.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No commands found for "{search}"
              </Typography>
            </Box>
          )}
        </List>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: alpha('#F8FAFC', 0.5),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="↑↓" size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">Navigate</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="↵" size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">Select</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            <Command size={12} color="#64748B" />
            <Typography variant="caption" color="text.secondary">K to open</Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export default CommandPalette;
